import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Grid,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Skeleton,
  CircularProgress
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as AttachMoneyIcon,
  BugReport as ForecastIcon,
  AccountBalance as BudgetIcon,
  Schedule as ScheduleIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Pending as PendingIcon
} from '@mui/icons-material';
import * as api from '../../api/api';
import { calculatePlanBreakEven, calculatePlanSavings, formatCurrency } from './planningCalculations';
import { buttonStyles, formControlStyles, layoutStyles } from '../../styles/commonStyles';
import { ActionButton, BackButton, StyledFormControl, PageHeader, ElevatedCard } from '../shared/StyledComponents';

// Lazy load heavy components for better initial performance
const OutcomeDetailsModal = React.lazy(() => import('./OutcomeDetailsModal'));


const PLAN_STATUSES = [
  'Draft',
  'Under Review', 
  'Approved',
  'Rejected'
];

// Skeleton loading components for better perceived performance
const PlanCardSkeleton = () => (
  <Card elevation={2} sx={{ mb: 3 }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Skeleton width={200} height={32} sx={{ mb: 1 }} />
            <Skeleton width={300} height={20} />
          </Box>
          <Skeleton width={80} height={24} sx={{ borderRadius: 12 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} width={40} height={40} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
      </Box>
      
      <Card elevation={0} sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            {[...Array(5)].map((_, i) => (
              <Grid item xs={12} md={2.4} key={i}>
                <Skeleton width={100} height={16} sx={{ mb: 1 }} />
                <Skeleton width={120} height={32} sx={{ mb: 1 }} />
                <Skeleton width={80} height={16} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </CardContent>
  </Card>
);

const FiltersSkeleton = () => (
  <Card elevation={1} sx={{ mb: 3 }}>
    <CardContent>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <Skeleton width="100%" height={40} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Skeleton width={200} height={20} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Skeleton width={150} height={20} />
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

// Memoized Plan Card Component for better performance
const PlanCard = React.memo(({ 
  plan, 
  budgetStatus, 
  breakEven, 
  savings, 
  theme,
  formatCurrency,
  getSafeBudgetValue,
  getOutcomeBudgetSource,
  memoizedAllOutcomes,
  getStatusIcon,
  getStatusColor,
  handleViewForecastVsActuals,
  onEditPlan,
  handleCopyPlan,
  openDeleteDialog,
  isReadOnly,
  alpha
}) => {
  // Memoize budget source calculation
  const budgetSourceInfo = useMemo(() => {
    const sourceInfo = (plan.selected_outcomes || []).map(outcomeId => {
      const source = getOutcomeBudgetSource(plan, outcomeId, memoizedAllOutcomes.outcomes);
      return { outcomeId, source: source.source, sourceLabel: source.sourceLabel };
    }).filter(info => info.source !== 'declined');
    
    const sources = sourceInfo.map(info => info.source);
    const uniqueSources = [...new Set(sources)];
    
    return { sourceInfo, sources, uniqueSources };
  }, [plan.selected_outcomes, plan.id, memoizedAllOutcomes.outcomes]);

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        {/* Plan Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {plan.name}
              </Typography>
              {plan.description && (
                <Typography variant="body2" color="text.secondary">
                  {plan.description}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getStatusIcon(plan.status)}
              <Chip
                label={plan.status || 'Draft'}
                color={getStatusColor(plan.status)}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="View Forecast vs Actuals">
              <Button
                variant="outlined"
                size="small"
                startIcon={<ForecastIcon />}
                onClick={() => handleViewForecastVsActuals(plan.id)}
                color="primary"
              >
                Forecast vs Actuals
              </Button>
            </Tooltip>
                        <Tooltip title={isReadOnly ? "Read-only mode" : "Edit Plan"}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => onEditPlan(plan.id)}
                  color="primary"
                  disabled={isReadOnly}
                >
                  Plan
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={isReadOnly ? "Read-only mode" : "Copy Plan"}>
              <span>
                <IconButton
                  onClick={() => handleCopyPlan(plan)}
                  color="secondary"
                  size="small"
                  disabled={isReadOnly}
                >
                  <CopyIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isReadOnly ? "Read-only mode" : "Delete Plan"}>
              <span>
                <IconButton
                  onClick={() => openDeleteDialog(plan)}
                  color="error"
                  size="small"
                  disabled={isReadOnly}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Modern Budget Summary */}
        <Card 
          elevation={0} 
          sx={{ 
            mb: 3, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <CardContent sx={{ pb: '16px !important' }}>
            <Grid container spacing={3}>
              {/* Total Budget */}
              <Grid item xs={12} md={2.4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoneyIcon 
                    sx={{ 
                      mr: 1, 
                      color: theme.palette.primary.main,
                      fontSize: '1.2rem'
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Total Budget
                  </Typography>
                  {/* Show mixed source indicator if outcomes have different sources */}
                  {(() => {
                    if (budgetSourceInfo.uniqueSources.length > 1) {
                      const sourceBreakdown = budgetSourceInfo.uniqueSources.map(source => {
                        const count = budgetSourceInfo.sources.filter(s => s === source).length;
                        const label = source === 'approved' ? 'Approved' : 
                                    source === 'adjusted' ? 'Adjusted' : 'Estimated';
                        return `${count} ${label}`;
                      }).join(', ');
                      
                      return (
                        <Tooltip title={`Mixed sources: ${sourceBreakdown}`}>
                          <Chip
                            label="Mixed"
                            size="small"
                            variant="outlined"
                            color="warning"
                            sx={{ fontSize: '0.6rem', height: '16px', cursor: 'help', ml: 0.5 }}
                          />
                        </Tooltip>
                      );
                    } else if (budgetSourceInfo.uniqueSources.length === 1) {
                      const source = budgetSourceInfo.uniqueSources[0];
                      const sourceLabel = source === 'approved' ? 'Approved' : 
                                        source === 'adjusted' ? 'Adjusted' : 'Estimated';
                      return (
                        <Tooltip title={`All ${budgetSourceInfo.sourceInfo.length} outcomes use ${sourceLabel.toLowerCase()} values`}>
                          <Chip
                            label={sourceLabel}
                            size="small"
                            variant="outlined"
                            color={
                              source === 'approved' ? 'success' :
                              source === 'adjusted' ? 'warning' : 'info'
                            }
                            sx={{ fontSize: '0.6rem', height: '16px', cursor: 'help', ml: 0.5 }}
                          />
                        </Tooltip>
                      );
                    }
                    return null;
                  })()
                  }
                </Box>
                <Typography 
                  variant="h4" 
                  fontWeight={700}
                  color="primary.main"
                  sx={{ lineHeight: 1.2 }}
                >
                  {formatCurrency(getSafeBudgetValue(plan))}
                </Typography>
                {/* Show corruption warning if budget was sanitized */}
                {plan.total_budget && plan.total_budget > 100000000 && !plan._calculatedTotalBudget && (
                  <Typography 
                    variant="caption" 
                    color="warning.main"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    ⚠️ Budget data corrupted, recalculating...
                  </Typography>
                )}
              </Grid>

              {/* Target Budget */}
              <Grid item xs={12} md={2.4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BudgetIcon 
                    sx={{ 
                      mr: 1, 
                      color: theme.palette.text.secondary,
                      fontSize: '1.2rem'
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Target Budget
                  </Typography>
                </Box>
                <Typography 
                  variant="h5" 
                  fontWeight={600}
                  color="text.primary"
                  sx={{ lineHeight: 1.2 }}
                >
                  {plan.target_budget ? formatCurrency(plan.target_budget) : 'Not set'}
                </Typography>
              </Grid>

              {/* Budget Status */}
              <Grid item xs={12} md={2.4}>
                {budgetStatus ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {budgetStatus.isOver ? (
                        <TrendingUpIcon 
                          sx={{ 
                            mr: 1, 
                            color: theme.palette.error.main,
                            fontSize: '1.2rem'
                          }} 
                        />
                      ) : (
                        <TrendingDownIcon 
                          sx={{ 
                            mr: 1, 
                            color: theme.palette.success.main,
                            fontSize: '1.2rem'
                          }} 
                        />
                      )}
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Budget Status
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography 
                          variant="h6" 
                          fontWeight={600}
                          color={budgetStatus.isOver ? 'error.main' : 'success.main'}
                          sx={{ lineHeight: 1.2, mb: 0.5 }}
                        >
                          {budgetStatus.text}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color={budgetStatus.isOver ? 'error.main' : 'success.main'}
                          fontWeight={500}
                        >
                          {formatCurrency(budgetStatus.difference)}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(budgetStatus.percentage, 100)}
                            color={budgetStatus.isOver ? 'error' : 'success'}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: alpha(
                                budgetStatus.isOver ? theme.palette.error.main : theme.palette.success.main, 
                                0.2
                              )
                            }}
                          />
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            {Math.round(budgetStatus.percentage)}% {budgetStatus.isOver ? 'over' : 'under'} target
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <BudgetIcon 
                        sx={{ 
                          mr: 1, 
                          color: theme.palette.text.secondary,
                          fontSize: '1.2rem'
                        }} 
                      />
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Budget Status
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                      Set target budget to see status
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Break-even Analysis */}
              <Grid item xs={12} md={2.4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ScheduleIcon 
                    sx={{ 
                      mr: 1, 
                      color: theme.palette.info.main,
                      fontSize: '1.2rem'
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Break-even
                  </Typography>
                </Box>
                {breakEven ? (
                  <Box>
                    <Typography 
                      variant="h6" 
                      fontWeight={600}
                      color="info.main"
                      sx={{ lineHeight: 1.2, mb: 0.5 }}
                    >
                      {breakEven.simpleBreakEvenMonths.toFixed(1)} months
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Simple: {formatCurrency(breakEven.totalDevelopmentCost)} ÷ {formatCurrency(breakEven.totalAnnualSavings)}/yr
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                    >
                      Weighted avg: {breakEven.weightedBreakEvenMonths.toFixed(1)} months
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No financial data available
                  </Typography>
                )}
              </Grid>

              {/* 1-Year Savings */}
              <Grid item xs={12} md={2.4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingDownIcon 
                    sx={{ 
                      mr: 1, 
                      color: theme.palette.success.main,
                      fontSize: '1.2rem'
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    1-Year Savings
                  </Typography>
                </Box>
                {savings && savings.contributingOutcomes > 0 ? (
                  <Box>
                    <Typography 
                      variant="h6" 
                      fontWeight={600}
                      color="success.main"
                      sx={{ lineHeight: 1.2, mb: 0.5 }}
                    >
                      {formatCurrency(savings.totalSavings)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="success.main"
                      fontWeight={500}
                      sx={{ mb: 1 }}
                    >
                      {savings.savingsPercentage.toFixed(1)}% savings
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                    >
                      {savings.contributingOutcomes} of {savings.totalOutcomes} outcomes contributing
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1" color="text.secondary">
                      No savings data available
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                    >
                      Add roles & efficiency gains to outcomes
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Year Badge */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                label={`Year ${plan.year}`}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for optimal re-rendering
  return (
    prevProps.plan.id === nextProps.plan.id &&
    prevProps.plan.name === nextProps.plan.name &&
    prevProps.plan.status === nextProps.plan.status &&
    prevProps.plan.total_budget === nextProps.plan.total_budget &&
    prevProps.plan._calculatedTotalBudget === nextProps.plan._calculatedTotalBudget &&
    prevProps.plan.target_budget === nextProps.plan.target_budget &&
    JSON.stringify(prevProps.budgetStatus) === JSON.stringify(nextProps.budgetStatus) &&
    JSON.stringify(prevProps.breakEven) === JSON.stringify(nextProps.breakEven) &&
    JSON.stringify(prevProps.savings) === JSON.stringify(nextProps.savings) &&
    prevProps.isReadOnly === nextProps.isReadOnly
  );
});

function PlanningPage({ onCreatePlan, onEditPlan, onBackToHome, isReadOnly = false, selectedDepartment = '' }) {
  const theme = useTheme();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  
  // Progressive loading states
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [dataLoadingPhase, setDataLoadingPhase] = useState('initial'); // 'initial', 'supporting', 'complete'
  const [availableOwners, setAvailableOwners] = useState({
    business: [],
    functional: [],
    data: [],
    it: []
  });
    const [planBreakEvens, setPlanBreakEvens] = useState({});
  const [planSavings, setPlanSavings] = useState({});
  const [colaRate, setCOLARate] = useState(4.5);
  
  

  // Filter states
  const [statusFilter, setStatusFilter] = useState('All');
  const [showApprovedOnly, setShowApprovedOnly] = useState(false);

  // Outcome details modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
    const [selectedOutcomeCostEstimation] = useState(null);
  const [selectedOutcomeFinancialModel] = useState(null);
  const [selectedOutcomePlanStatus, setSelectedOutcomePlanStatus] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [allOutcomes, setAllOutcomes] = useState({});
  
        // Performance optimization refs
  const calculationTimeoutRef = useRef(null);
  const lastCalculationRef = useRef(null);
  const dataVersionRef = useRef(0);
  const activeRequestsRef = useRef(new Map());

  // Listen for edit mode changes
  useEffect(() => {
    const handleEditModeChange = (event) => {
      // Force re-render when edit mode changes
      setError('');
    };

    window.addEventListener('editModeChanged', handleEditModeChange);
    
    return () => {
      window.removeEventListener('editModeChanged', handleEditModeChange);
    };
  }, []);

  // Fetch COLA settings
  useEffect(() => {
    const fetchCOLASettings = async () => {
      try {
        const settings = await api.getCOLASettings();
        setCOLARate(settings.annual_cola_percentage || 4.5);
      } catch (error) {
        console.warn('COLA settings endpoint not available, using default rate:', error);
        setCOLARate(4.5); // Default fallback - don't show error to user
      }
    };
    fetchCOLASettings();
  }, []);

      // Fetch all outcomes for the details modal - OPTIMIZED
  const fetchAllOutcomes = useCallback(async () => {
          try {
        // Use lightweight outcomes endpoint for better performance
                  console.log('PlanningPage: Using standard outcomes endpoint...');
          const startTime = performance.now();
          const response = await api.getOutcomes(selectedDepartment || null);
          const endTime = performance.now();
          console.log(`PlanningPage: Standard endpoint completed in ${(endTime - startTime).toFixed(2)}ms`);
          
          // Handle response format - getOutcomes returns {outcomes: [...]}
          const outcomes = response.outcomes || response || [];
        console.log('PlanningPage: Received response:', response);
        console.log('PlanningPage: Response type:', Array.isArray(response) ? 'Array' : 'Object');
        if (response && response.outcomes) {
          console.log('PlanningPage: Found', response.outcomes.length, 'outcomes in response.outcomes');
        } else if (Array.isArray(response)) {
          console.log('PlanningPage: Found', response.length, 'outcomes in array response');
        } else {
          console.log('PlanningPage: Unexpected response format');
        }
      let outcomesList = [];
      
      if (Array.isArray(response)) {
        outcomesList = response;
      } else if (response && response.outcomes && Array.isArray(response.outcomes)) {
        outcomesList = response.outcomes;
      } else if (response && response.data && Array.isArray(response.data.outcomes)) {
        outcomesList = response.data.outcomes;
      } else if (response && response.data && typeof response.data === 'object') {
        outcomesList = Object.values(response.data);
      }
      
      // Convert to object for easy lookup
      const outcomesObj = {};
      outcomesList.forEach(outcome => {
        if (outcome && outcome.id && !outcome.deleted) {
          outcomesObj[outcome.id] = outcome;
        }
      });
      
      // Only update if outcomes actually changed (optimized comparison)
      setAllOutcomes(prev => {
        const prevKeys = Object.keys(prev).sort();
        const newKeys = Object.keys(outcomesObj).sort();
        const keysChanged = prevKeys.join(',') !== newKeys.join(',');
        
        if (!keysChanged && prevKeys.length > 0) {
          // Check if any critical outcome data changed
          const dataChanged = prevKeys.some(outcomeId => {
            const prevOutcome = prev[outcomeId];
            const newOutcome = outcomesObj[outcomeId];
            return !prevOutcome || !newOutcome ||
                   prevOutcome.status !== newOutcome.status ||
                   (prevOutcome.approved_budget?.capex || 0) !== (newOutcome.approved_budget?.capex || 0) ||
                   (prevOutcome.approved_budget?.opex || 0) !== (newOutcome.approved_budget?.opex || 0);
          });
          
          if (!dataChanged) return prev;
        }
        
                console.log('PlanningPage: Final outcomes object has', Object.keys(outcomesObj).length, 'outcomes');
        console.log('PlanningPage: Outcome IDs:', Object.keys(outcomesObj).slice(0, 5));
        return outcomesObj;
      });
        } catch (error) {
      console.error('Error fetching outcomes:', error);
      // Set empty outcomes on error to prevent infinite loops
      setAllOutcomes({});
    }
  }, [selectedDepartment]); // eslint-disable-line react-hooks/exhaustive-deps

              // Helper function to calculate outcome budget (matching CreateEditPlan logic)
  const calculateOutcomeBudget = useCallback((plan, outcomeId, outcomeDetails, outcomesLookup, useAdjustment = true) => {
    const planStatus = plan.plan_statuses?.[outcomeId] || "Under Consideration";
    if (planStatus === "Declined") return 0;

    // Priority 1: Check for approved budget from outcome (total of CAPEX + OPEX)
    const outcome = outcomesLookup[outcomeId];
    if (outcome?.approved_budget && 
        (outcome.approved_budget.capex > 0 || outcome.approved_budget.opex > 0)) {
      return Math.round((outcome.approved_budget.capex || 0) + (outcome.approved_budget.opex || 0));
    }

    // Priority 2: Use manual budget adjustment if available and requested
    if (useAdjustment && plan.outcome_adjustments?.[outcomeId] !== undefined) {
      return plan.outcome_adjustments[outcomeId];
    }

    // Priority 3: Calculate from outcome details
    const details = outcomeDetails[outcomeId];
    if (!details || !details.costEstimation || !details.costEstimation.roles) return 0;

    const costEstimation = details.costEstimation;
    const baseRoleCost = costEstimation.roles.reduce((sum, role) => {
      const months = (role.dateRanges || []).reduce((m, dr) =>
        m + ((dr.endMonth || costEstimation.adjustedMonths || 6) - (dr.startMonth || 1) + 1) * ((dr.allocation || 100) / 100), 0
      );
      return sum + (role.rate || 0) * months;
    }, 0);

    const totalFixedCost = (costEstimation.fixedCosts || []).reduce((sum, cost) => {
      return sum + (cost.monthlyCost || 0) * (cost.duration || costEstimation.adjustedMonths || 6);
    }, 0);

    return Math.round(baseRoleCost + totalFixedCost);
  }, []);

        // Helper function to get budget source information for an outcome
  const getOutcomeBudgetSource = useCallback((plan, outcomeId, outcomesLookup) => {
    const planStatus = plan.plan_statuses?.[outcomeId] || "Under Consideration";
    if (planStatus === "Declined") {
      return { source: 'declined', sourceLabel: 'Declined' };
    }

    // Check for approved budget
    const outcome = outcomesLookup[outcomeId];
    if (outcome?.approved_budget && 
        (outcome.approved_budget.capex > 0 || outcome.approved_budget.opex > 0)) {
      return { source: 'approved', sourceLabel: 'Approved' };
    }

    // Check for manual budget adjustment (legacy or new)
    if (plan.outcome_adjustments?.[outcomeId] !== undefined ||
        plan.capex_adjustments?.[outcomeId] !== undefined || 
        plan.opex_adjustments?.[outcomeId] !== undefined) {
      return { source: 'adjusted', sourceLabel: 'Adjusted' };
    }

    // Default to estimated
    return { source: 'estimated', sourceLabel: 'Estimated' };
  }, []);

          // Memoize allOutcomes with stable reference
  const memoizedAllOutcomes = useMemo(() => {
    const outcomeKeys = Object.keys(allOutcomes).sort().join(',');
    return { outcomes: allOutcomes, version: outcomeKeys };
  }, [allOutcomes]);

        // Optimized calculation function with better caching
  const calculateBreakEvensAndSavingsForPlans = useCallback(async (plansData) => {
    // Clear any existing timeout
    if (calculationTimeoutRef.current) {
      clearTimeout(calculationTimeoutRef.current);
    }
    
    // Create a hash of the current data to avoid unnecessary recalculations
    const dataHash = JSON.stringify({
      planIds: plansData.map(p => p.id).sort(),
      planVersions: plansData.map(p => `${p.id}-${p.total_budget}-${p.selected_outcomes?.length || 0}`).sort(),
      outcomesVersion: memoizedAllOutcomes.version,
      colaRate
    });
    
    // Skip if data hasn't changed
    if (lastCalculationRef.current === dataHash) {
      return;
    }
    
    lastCalculationRef.current = dataHash;
    
    // Debounce the calculation
    calculationTimeoutRef.current = setTimeout(async () => {
      const breakEvens = {};
      const savings = {};
      
            // Process plans in batches to avoid blocking the UI
      const batchSize = 5;
      for (let i = 0; i < plansData.length; i += batchSize) {
        const batch = plansData.slice(i, i + batchSize);
        
                await Promise.all(batch.map(async (plan) => {
          try {
            // Fetch outcome details for this plan
            const outcomeDetails = {};
            const outcomePromises = (plan.selected_outcomes || []).map(async (outcomeId) => {
              try {
                // Check if request is already in progress
                const requestKey = `${outcomeId}_plan_details`;
                if (activeRequestsRef.current.has(requestKey)) {
                  const existingRequest = activeRequestsRef.current.get(requestKey);
                  const result = await existingRequest;
                  outcomeDetails[outcomeId] = result;
                  return;
                }
                
                // Create new request and cache it
                const requestPromise = Promise.all([
                  api.getCostEstimation(outcomeId).catch(() => null),
                  api.getFinancialModel(outcomeId).catch(() => null)
                ]).then(([costEstimation, financialModel]) => ({
                  costEstimation: costEstimation?.data || costEstimation,
                  financialModel: financialModel?.data || financialModel
                })).finally(() => {
                  activeRequestsRef.current.delete(requestKey);
                });
                
                activeRequestsRef.current.set(requestKey, requestPromise);
                outcomeDetails[outcomeId] = await requestPromise;
              } catch (error) {
                outcomeDetails[outcomeId] = {};
              }
            });
            
            await Promise.all(outcomePromises);
          
                                              // Calculate total budget using the same logic as CreateEditPlan
            const totalBudget = (plan.selected_outcomes || []).reduce((total, outcomeId) => {
              const outcomeTotal = calculateOutcomeBudget(plan, outcomeId, outcomeDetails, memoizedAllOutcomes.outcomes);
              return total + outcomeTotal;
            }, 0);

            // Auto-fix severely corrupted data (difference > 1B suggests corruption)
            if (plan.total_budget && Math.abs(totalBudget - plan.total_budget) > 1000000000 && 
                totalBudget > 0 && totalBudget < 100000000) {
              api.updatePlan(plan.id, { ...plan, total_budget: totalBudget })
                .catch(error => console.error(`Failed to auto-fix budget for plan "${plan.name}":`, error));
            }

            // Always use calculated value, ignore potentially corrupted stored value
            plan._calculatedTotalBudget = totalBudget;
          
                    // Calculate break-even for this plan
          const breakEven = calculatePlanBreakEven(
            plan.selected_outcomes || [],
            outcomeDetails,
            plan.plan_statuses || {},
            plan.outcome_adjustments || {},
            memoizedAllOutcomes.outcomes
          );
          
          if (breakEven) {
            breakEvens[plan.id] = breakEven;
          }

                    // Calculate 1-year savings for this plan
          const planSaving = calculatePlanSavings(
            plan.selected_outcomes || [],
            outcomeDetails,
            plan.plan_statuses || {},
            plan.outcome_adjustments || {},
            colaRate,
            memoizedAllOutcomes.outcomes
          );
          
          if (planSaving) {
            savings[plan.id] = planSaving;
          }
                  } catch (error) {
            console.warn(`Error calculating metrics for plan ${plan.id}:`, error);
          }
        }));
        
        // Small delay between batches to keep UI responsive
        if (i + batchSize < plansData.length) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      
                  // Update state only if values changed (optimized comparison)
      setPlanBreakEvens(prev => {
        const hasChanged = JSON.stringify(prev) !== JSON.stringify(breakEvens);
        return hasChanged ? breakEvens : prev;
      });
      
      setPlanSavings(prev => {
        const hasChanged = JSON.stringify(prev) !== JSON.stringify(savings);
        return hasChanged ? savings : prev;
      });
                                }, 300); // Optimized debounce time
  }, [colaRate, memoizedAllOutcomes.outcomes]); // Remove calculateOutcomeBudget to prevent infinite loop

                            // Standard plans fetching
  const fetchPlans = useCallback(async (skipCalculations = false) => {
    try {
      setLoading(true);
      const startTime = performance.now();
      
      // Use standard plans endpoint
      const response = await api.getPlans();
      let plansData = response.plans || [];
      
      const endTime = performance.now();
      const clientLoadTime = endTime - startTime;
      
      
      
      // Increment data version for cache invalidation
      dataVersionRef.current += 1;
      
      // Optimized plan update with shallow comparison
      setPlans(prev => {
        // Quick length check first
        if (prev.length !== plansData.length) {
          if (!skipCalculations) {
            setTimeout(() => calculateBreakEvensAndSavingsForPlans(plansData), 100);
          }
          return plansData;
        }
        
        // Check for actual changes in plan data
        const hasChanged = prev.some((prevPlan, index) => {
          const newPlan = plansData[index];
          return !newPlan || 
                 prevPlan.id !== newPlan.id ||
                 prevPlan.total_budget !== newPlan.total_budget ||
                 prevPlan.status !== newPlan.status ||
                 JSON.stringify(prevPlan.selected_outcomes) !== JSON.stringify(newPlan.selected_outcomes);
        });
        
        if (hasChanged && !skipCalculations) {
          setTimeout(() => calculateBreakEvensAndSavingsForPlans(plansData), 100);
        }
        
        return hasChanged ? plansData : prev;
      });
      
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []); // Remove dependency to prevent infinite loop

      const fetchOwners = useCallback(async () => {
      try {
        console.log('PlanningPage: Fetching owners...');
        const startTime = performance.now();
        const data = await api.getOwners();
        const endTime = performance.now();
        console.log(`PlanningPage: Owners loaded in ${(endTime - startTime).toFixed(2)}ms`);
        setAvailableOwners(data);
    } catch (error) {
      console.error('Error fetching owners:', error);
      setAvailableOwners({
        business: [],
        functional: [],
        data: [],
        it: []
      });
    }
  }, []);

          

            // Enhanced progressive data loading with better performance
  useEffect(() => {
    let isMounted = true;
    let loadingTimeout;
    
    const loadInitialData = async () => {
      try {
        console.log('PlanningPage: Starting progressive data load...');
        
        // Phase 1: Critical data only (plans for immediate UI)
        console.log('PlanningPage: Phase 1 - Loading critical data...');
        setLoading(true);
        
        // Load plans without calculations first
        await fetchPlans(true);
        
        if (!isMounted) return;
        
        // Set loading to false early to show UI
        setLoading(false);
        setDataLoadingPhase('supporting');
        
        // Phase 2: Background data loading (non-blocking)
        setTimeout(async () => {
          if (!isMounted) return;
          
          console.log('PlanningPage: Phase 2 - Loading background data...');
          setBackgroundLoading(true);
          
          // Load supporting data in parallel
          const backgroundTasks = [
            fetchOwners().catch(err => {
              console.warn('Failed to load owners:', err);
              return null;
            }),
            fetchAllOutcomes().catch(err => {
              console.warn('Failed to load outcomes:', err);
              return null;
            })
          ];
          
          await Promise.allSettled(backgroundTasks);
          
          if (isMounted) {
            setBackgroundLoading(false);
            setDataLoadingPhase('complete');
            
            // Phase 3: Calculations (lowest priority)
            console.log('PlanningPage: Phase 3 - Starting calculations...');
            setTimeout(() => {
              if (isMounted) {
                fetchPlans(false); // Now with calculations
              }
            }, 300); // Reduced delay
          }
        }, 100); // Reduced initial delay
        
      } catch (error) {
        console.error('Error in initial data load:', error);
        if (isMounted) {
          setError('Failed to load initial data');
          setLoading(false);
        }
      }
    };
    
    // Set maximum loading timeout
    loadingTimeout = setTimeout(() => {
      if (isMounted) {
        console.warn('PlanningPage: Loading timeout reached, forcing completion');
        setLoading(false);
      }
    }, 8000); // 8 second timeout
    
    loadInitialData();
    
    return () => {
      isMounted = false;
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, []); // Remove dependencies to prevent infinite loop

  // Re-fetch outcomes when global department filter changes
  useEffect(() => {
    fetchAllOutcomes();
  }, [fetchAllOutcomes]);

    // Optimized localStorage update with debouncing
  useEffect(() => {
    if (plans.length > 0) {
      const timeoutId = setTimeout(() => {
        try {
          localStorage.setItem('currentPlans', JSON.stringify(plans));
        } catch (error) {
          console.warn('Failed to save plans to localStorage:', error);
        }
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [plans]);

    const handleCopyPlan = async (plan) => {
    if (isReadOnly) {
      setError('Cannot copy plans in read-only mode');
      return;
    }

    try {
      const copiedPlan = await api.copyPlan(plan.id);
      await fetchPlans(false); // Allow calculations for new plan
      onEditPlan(copiedPlan.plan.id);
    } catch (error) {
      console.error('Error copying plan:', error);
      setError('Failed to copy plan');
    }
  };

    const handleDeletePlan = async () => {
    if (isReadOnly) {
      setError('Cannot delete plans in read-only mode');
      return;
    }

    if (!planToDelete) return;
    
    try {
      await api.deletePlan(planToDelete.id);
      await fetchPlans(false); // Allow calculations after deletion
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
    } catch (error) {
      console.error('Error deleting plan:', error);
      setError('Failed to delete plan');
    }
  };

  const openDeleteDialog = (plan) => {
    if (isReadOnly) {
      setError('Cannot delete plans in read-only mode');
      return;
    }

    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const handleViewForecastVsActuals = (planId) => {
    // Store the selected plan ID for the ForecastVsActuals component
    localStorage.setItem('selectedPlanForForecast', planId);
    
    // Trigger a custom event to notify other components
    window.dispatchEvent(new CustomEvent('planSelectedForForecast', { 
      detail: { planId } 
    }));
    
    // Switch to the Forecast vs Actuals tab (tab index 3)
    window.dispatchEvent(new CustomEvent('switchToTab', { 
      detail: { tabIndex: 3 } 
    }));
  };

  const handlePlanStatusChange = async (newStatus) => {
    if (!selectedOutcome || !selectedPlan || isReadOnly) return;

    try {
      const updatedPlan = {
        ...selectedPlan,
        plan_statuses: {
          ...selectedPlan.plan_statuses,
          [selectedOutcome.id]: newStatus
        }
      };

      await api.updatePlan(selectedPlan.id, updatedPlan);
      setSelectedOutcomePlanStatus(newStatus);
      
            // Update local state and trigger recalculation
      setPlans(prev => {
        const newPlans = prev.map(p => p.id === selectedPlan.id ? updatedPlan : p);
        // Trigger recalculation for the updated plan
        setTimeout(() => {
          calculateBreakEvensAndSavingsForPlans([updatedPlan]);
        }, 100);
        return newPlans;
      });
      
      setSelectedPlan(updatedPlan);
    } catch (error) {
      console.error('Error updating plan status:', error);
      setError('Failed to update plan status');
    }
  };

  const handleOutcomeStatusChange = async (newStatus) => {
    if (!selectedOutcome || isReadOnly) return;

    try {
      const updatedOutcome = { ...selectedOutcome, status: newStatus };
      await api.updateOutcome(selectedOutcome.id, updatedOutcome);
      
      setSelectedOutcome(updatedOutcome);
      
      // Update local outcomes cache
      setAllOutcomes(prev => ({
        ...prev,
        [selectedOutcome.id]: updatedOutcome
      }));

      // Trigger refresh event for other components
      window.dispatchEvent(new CustomEvent('outcomeStatusUpdated', { 
        detail: { outcomeId: selectedOutcome.id, newStatus } 
      }));
    } catch (error) {
      console.error('Error updating outcome status:', error);
      setError('Failed to update outcome status');
    }
  };

        // Memoized helper functions for better performance
  const getBudgetStatus = useCallback((plan) => {
    if (!plan.target_budget) return null;
    
    const totalBudget = getSafeBudgetValue(plan);
    const difference = totalBudget - plan.target_budget;
    const isOver = difference > 0;
    const percentage = Math.abs((difference / plan.target_budget) * 100);
    
    return {
      isOver,
      difference: Math.abs(difference),
      percentage: Math.min(percentage, 100), // Cap at 100% for display
      color: isOver ? 'error' : 'success',
      text: isOver ? 'Over Budget' : 'Under Budget'
    };
  }, []);
  
  // Helper function to get safe budget value (prevents displaying corrupted data)
  const getSafeBudgetValue = useCallback((plan) => {
    const calculatedBudget = plan._calculatedTotalBudget;
    const storedBudget = plan.total_budget;
    
    // If we have a calculated budget, use it
    if (calculatedBudget !== undefined) {
      return calculatedBudget;
    }
    
    // If stored budget seems corrupted (> 100M), return 0 instead
    if (storedBudget && storedBudget > 100000000) {
      console.warn(`Preventing display of corrupted budget for plan "${plan.name}": ${storedBudget}`);
      return 0;
    }
    
    return storedBudget || 0;
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'Approved':
        return <ApproveIcon sx={{ color: theme.palette.success.main }} />;
      case 'Rejected':
        return <RejectIcon sx={{ color: theme.palette.error.main }} />;
      case 'Under Review':
        return <PendingIcon sx={{ color: theme.palette.warning.main }} />;
      default:
        return <PendingIcon sx={{ color: theme.palette.text.secondary }} />;
    }
  }, [theme]);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Under Review':
        return 'warning';
      default:
        return 'default';
    }
  }, []);

    // Memoize filtered plans to prevent unnecessary re-renders
  const filteredPlans = useMemo(() => {
    return plans.filter(plan => {
      if (showApprovedOnly && plan.status !== 'Approved') {
        return false;
      }
      
      if (statusFilter !== 'All' && plan.status !== statusFilter) {
        return false;
      }
      
      return true;
    });
  }, [plans, showApprovedOnly, statusFilter]);

    // Enhanced cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear all timeouts
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
      
      // Clear active requests to prevent memory leaks
      activeRequestsRef.current.forEach((request, key) => {
        if (request && typeof request.cancel === 'function') {
          request.cancel();
        }
      });
      activeRequestsRef.current.clear();
    };
  }, []);

    // Enhanced loading state with skeleton
  if (loading) {
    return (
      <Box>
        {/* Header Skeleton */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Skeleton width={150} height={40} sx={{ mb: 1 }} />
            <Skeleton width={300} height={20} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Skeleton width={120} height={36} />
            <Skeleton width={150} height={36} />
          </Box>
        </Box>
        
        {/* Filters Skeleton */}
        <FiltersSkeleton />
        
        {/* Plan Cards Skeleton */}
        {[...Array(3)].map((_, i) => (
          <PlanCardSkeleton key={i} />
        ))}
      </Box>
    );
  }

    return (
    <Box>
      
      
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {onBackToHome && (
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={onBackToHome}
              >
                Home
              </Button>
            )}
            <Box>
              <Typography variant="h4">Planning</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Create and manage outcome planning scenarios
                {isReadOnly && ' (Read-Only Mode)'}
              </Typography>
              {backgroundLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CircularProgress size={12} sx={{ mr: 1 }} />
                  <Typography variant="caption" color="info.main">
                    Loading additional data in background...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ActionButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreatePlan}
              disabled={isReadOnly}
              sx={{
                bgcolor: isReadOnly ? 'action.disabled' : 'primary.main',
                '&:hover': {
                  bgcolor: isReadOnly ? 'action.disabled' : 'primary.dark'
                }
              }}
            >
              Create New Plan
            </ActionButton>
          </Box>
        </Box>

      {/* Filters */}
      <Card elevation={1} sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
              <StyledFormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="All">All Statuses</MenuItem>
                  {PLAN_STATUSES.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </StyledFormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showApprovedOnly}
                    onChange={(e) => setShowApprovedOnly(e.target.checked)}
                    color="primary"
                  />
                }
                label="Show Approved Plans Only"
              />
            </Grid>
                        <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredPlans.length} of {plans.length} plans
                </Typography>
                {dataLoadingPhase !== 'complete' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {backgroundLoading && <CircularProgress size={10} />}
                    <Typography variant="caption" color="text.secondary">
                      {dataLoadingPhase === 'supporting' ? 'Loading...' : 'Calculating...'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {filteredPlans.length === 0 ? (
        <Card elevation={2}>
          <CardContent>
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>
                {plans.length === 0 ? 'No plans created yet' : 'No plans match the current filters'}
              </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {plans.length === 0 
                  ? 'Create your first planning scenario to get started'
                  : 'Try adjusting your filters or create a new plan'
                }
              </Typography>
              <ActionButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreatePlan}
                disabled={isReadOnly}
                sx={{ 
                  bgcolor: isReadOnly ? 'action.disabled' : 'primary.main',
                  '&:hover': {
                    bgcolor: isReadOnly ? 'action.disabled' : 'primary.dark'
                  }
                }}
              >
                Create New Plan
              </ActionButton>
            </Box>
          </CardContent>
        </Card>
      ) : (
                                filteredPlans.map((plan) => {
          const budgetStatus = getBudgetStatus(plan);
          const breakEven = planBreakEvens[plan.id];
          const savings = planSavings[plan.id];
          
          return (
            <PlanCard
              key={plan.id}
              plan={plan}
              budgetStatus={budgetStatus}
              breakEven={breakEven}
              savings={savings}
              theme={theme}
              formatCurrency={formatCurrency}
              getSafeBudgetValue={getSafeBudgetValue}
              getOutcomeBudgetSource={getOutcomeBudgetSource}
              memoizedAllOutcomes={memoizedAllOutcomes}
              getStatusIcon={getStatusIcon}
              getStatusColor={getStatusColor}
              handleViewForecastVsActuals={handleViewForecastVsActuals}
              onEditPlan={onEditPlan}
              handleCopyPlan={handleCopyPlan}
              openDeleteDialog={openDeleteDialog}
              isReadOnly={isReadOnly}
              alpha={alpha}
            />
              
          );
        })
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the plan "{planToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeletePlan} 
            color="error" 
            variant="contained"
            disabled={isReadOnly}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

            {/* Outcome Details Modal with Suspense */}
      <React.Suspense fallback={
        <Dialog open={detailsModalOpen} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          </DialogTitle>
        </Dialog>
      }>
        <OutcomeDetailsModal
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          outcome={selectedOutcome}
          costEstimation={selectedOutcomeCostEstimation}
          financialModel={selectedOutcomeFinancialModel}
          owners={availableOwners}
          planStatus={selectedOutcomePlanStatus}
          onPlanStatusChange={handlePlanStatusChange}
          onOutcomeStatusChange={handleOutcomeStatusChange}
          showPlanStatus={true}
          readOnly={isReadOnly}
        />
      </React.Suspense>
    </Box>
  );
}

export default memo(PlanningPage);